import { Box, Flex, FlexProps } from "styled-system/jsx";
import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import { css } from "styled-system/css";

const LEFT_PANEL_PADDING_DEFAULT = css({
  paddingBottom: "200",
});
const LEFT_PANEL_PADDING_OVERFLOWING = css({
  paddingBottom: { base: "200", md: "500" },
});

interface AppLayoutProps {
  sidebar: ReactNode;
  content: ReactNode;
  allowMobileShrink?: boolean;
  contentContainerProps?: FlexProps;
  contentWidth?: "sm" | "md" | "lg" | "full";
}

export const AppLayout = forwardRef<HTMLDivElement, AppLayoutProps>(
  function AppLayout(
    { sidebar, content, contentContainerProps, allowMobileShrink = false, contentWidth = "lg" },
    ref,
  ) {
    // Dynamically add padding to the bottom of the left panel if it is overflowing
    // We start with a small amout of padding to avoid scrollbars when possible
    const leftPanelRef = useRef<HTMLDivElement>(null);
    const [leftPanelClassName, setLeftPanelClassName] = useState(
      LEFT_PANEL_PADDING_DEFAULT,
    );
    useEffect(() => {
      if (!leftPanelRef.current) return;

      const content = leftPanelRef.current;

      const updatePadding = () => {
        const isOverflowing = content.scrollHeight > content.clientHeight;
        setLeftPanelClassName(
          isOverflowing
            ? LEFT_PANEL_PADDING_OVERFLOWING
            : LEFT_PANEL_PADDING_DEFAULT,
        );
      };

      updatePadding();

      // Watch for children being added/removed or subtree changes
      const mutationObserver = new MutationObserver(() => {
        updatePadding();
      });

      mutationObserver.observe(content, {
        childList: true, // watch direct children added/removed
        subtree: true, // watch descendants as well
      });

      return () => mutationObserver.disconnect();
    }, []);

    return (
      <Flex
        flexDir={{
          base: allowMobileShrink ? "column" : "row",
          md: "row",
        }}
        w={"full"}
        h={"full"}
        overflow="auto"
      >
        <Flex
          bg={{
            base: allowMobileShrink ? "bg.wa.light" : "bg.wa.lightest",
            md: "bg.wa.lightest",
          }}
          borderRightWidth={{
            base: allowMobileShrink ? "0px" : "1px",
            md: "1px",
          }}
          borderRightColor={{
            base: allowMobileShrink ? "transparent" : "border.light",
            md: "border.light",
          }}
          shrink={0}
          w={{
            base: allowMobileShrink ? "full" : "auto",
            md: "auto",
          }}
          borderBottom={{
            base: allowMobileShrink ? "1px solid" : "none",
            md: "none",
          }}
          borderBottomColor={{
            base: allowMobileShrink ? "border.light" : "transparent",
            md: "transparent",
          }}
          position={{
            base: allowMobileShrink ? "sticky" : "static",
            md: "static",
          }}
          top={0}
          zIndex={10}
        >
          <Box
            ref={leftPanelRef}
            pt={{ base: "250", md: "1000" }}
            px={{ base: "300", md: "600" }}
            overflowY={{
              base: allowMobileShrink ? "hidden" : "auto",
              md: "auto",
            }}
            w={"full"}
            transition="all 0.2s ease-in-out"
            className={leftPanelClassName}
          >
            {sidebar}
          </Box>
        </Flex>
        <Flex bg={"bg.wa.light"} grow={1} minW={0} {...contentContainerProps}>
          <Box
            ref={ref}
            py={{ base: "0", md: "200" }}
            px={{ base: "300", md: "60px" }}
            w={"full"}
            minW={0}
            position="relative"
            id="app-layout__main-content"
            transition="all 0.2s ease-in-out"
            overflowY="auto"
            overflowX="hidden"
          >
            <Box
              maxW={
                contentWidth === "full"
                  ? "100%"
                  : contentWidth === "sm"
                    ? "min(600px, 100%)"
                    : contentWidth === "md"
                      ? "min(768px, 100%)"
                      : "1024px"
              }
              mx={{ base: "0", md: contentWidth === "full" ? "0" : "auto" }}
              pt={{ base: "250", md: "800" }}
              pb={{ base: "600", md: "800" }}
            >
              {content}
            </Box>
          </Box>
        </Flex>
      </Flex>
    );
  },
);
